import axiosLib from "axios";
import { axios, transformAxiosError } from "../utils/axios.create";
import {
  ParticipantSearchItem,
  SessionStatusDto,
  TimelineEventDetail,
  TimelineQueryParams,
  TimelineResponse,
  ValidationJourney,
} from "../domain/troubleshooting.interface";

/**
 * COYN-1507: troubleshooting API (contract §4), gated by PART_TIMELINE_VIEW.
 * The axios instance is already scoped to `/accounts/{accountId}`, so every
 * call stays within the selected account.
 */
const BASE = "/troubleshooting/participants";

/** Error shape of this service: the shared transform plus the HTTP status. */
export interface TroubleshootingApiError {
  message?: string;
  error?: unknown;
  status?: number;
}

/**
 * Contract §15: keep the HTTP status that the shared `transformAxiosError`
 * drops, so the screen can tell a 403 (no PART_TIMELINE_VIEW on the backend)
 * from other failures. The shared interceptor/helper stay untouched.
 */
const toApiError = (err: unknown): unknown => {
  const transformed = transformAxiosError(err as Error);
  const status = axiosLib.isAxiosError(err) ? err.response?.status : undefined;
  return status === undefined
    ? transformed
    : { ...(transformed as object), status };
};

const participantPath = (participantId: string) =>
  `${BASE}/${encodeURIComponent(participantId)}`;

const unwrap = <T>(promise: Promise<{ data: { body: T } }>): Promise<T> =>
  promise
    .then((res) => res.data.body)
    .catch((err) => Promise.reject(toApiError(err)));

const searchParticipants = (q: string): Promise<ParticipantSearchItem[]> => {
  const qs = new URLSearchParams({ q: q.trim() }).toString();
  return unwrap<ParticipantSearchItem[]>(axios.get(`${BASE}/search?${qs}`));
};

const getTimeline = (
  participantId: string,
  params: TimelineQueryParams,
): Promise<TimelineResponse> => {
  const query: Record<string, string> = { from: params.from, to: params.to };
  if (params.lanes && params.lanes.length > 0) {
    query.lanes = params.lanes.join(",");
  }
  if (params.failuresOnly) query.failuresOnly = "true";
  const qs = new URLSearchParams(query).toString();
  return unwrap<TimelineResponse>(
    axios.get(`${participantPath(participantId)}/timeline?${qs}`),
  );
};

const getTimelineEvent = (
  participantId: string,
  eventId: string,
): Promise<TimelineEventDetail> =>
  unwrap<TimelineEventDetail>(
    axios.get(
      `${participantPath(participantId)}/timeline/${encodeURIComponent(eventId)}`,
    ),
  );

const getJourneys = (
  participantId: string,
  params: { from: string; to: string },
): Promise<ValidationJourney[]> => {
  const qs = new URLSearchParams({
    from: params.from,
    to: params.to,
  }).toString();
  return unwrap<ValidationJourney[]>(
    axios.get(`${participantPath(participantId)}/journeys?${qs}`),
  );
};

/** Contract §6.1: current (inferred) session state of the participant's app. */
const getSessionStatus = (participantId: string): Promise<SessionStatusDto> =>
  unwrap<SessionStatusDto>(
    axios.get(`${participantPath(participantId)}/session-status`),
  );

export const TroubleshootingService = {
  searchParticipants,
  getTimeline,
  getTimelineEvent,
  getJourneys,
  getSessionStatus,
};
